import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type { ApiResponse } from '@/types/user'

class ApiClient {
  private client: AxiosInstance
  private baseURL: string

  constructor() {
    // VITE_API_BASE_URL 配置说明：
    // - 开发环境: /api/v1（通过 Vite proxy，局域网手机预览也可用）
    // - Docker环境: /api/v1（通过nginx反代到后端）
    // - 生产环境（推荐）: /api/v1（同源，通过nginx反代）
    // - 生产环境（独立API域名）: https://api.yourdomain.com/api/v1
    this.baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        // 添加认证token
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        // 统一处理响应格式
        if (response.data.code === 200) {
          return response.data as any
        } else {
          // 业务逻辑错误
          return Promise.reject(new Error(response.data.message || '请求失败'))
        }
      },
      (error) => {
        // 网络错误或服务器错误
        if (error.response) {
          // 服务器返回错误状态码
          const { status, data } = error.response
          
          switch (status) {
            case 401:
              // 未授权，清除token并跳转到登录页
              localStorage.removeItem('token')
              localStorage.removeItem('user')
              window.location.href = '/login'
              break
            case 403:
              // 禁止访问
              console.error('权限不足')
              break
            case 404:
              // 资源不存在
              console.error('请求的资源不存在')
              break
            case 429:
              // 请求过于频繁
              console.error('请求过于频繁，请稍后再试')
              break
            case 500:
              // 服务器内部错误
              console.error('服务器内部错误')
              break
            default:
              console.error(`请求失败: ${status}`)
          }
          
          // 创建自定义错误对象，保留完整响应信息
          const customError = new Error(data?.message || data?.error || `请求失败: ${status}`) as any
          customError.response = customError.response || {}
          customError.response.data = data
          customError.response.status = status
          return Promise.reject(customError)
        } else if (error.request) {
          // 请求已发出但没有收到响应
          console.error('网络错误，请检查网络连接')
          return Promise.reject(new Error('网络错误，请检查网络连接'))
        } else {
          // 请求配置错误
          console.error('请求配置错误:', error.message)
          return Promise.reject(error)
        }
      }
    )
  }

  // 通用请求方法
  async request<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      return await this.client.request<ApiResponse<T>, ApiResponse<T>>(config)
    } catch (error) {
      throw error
    }
  }

  // GET请求
  async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'GET',
      url,
      params
    })
  }

  // POST请求
  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'POST',
      url,
      data
    })
  }

  // PUT请求
  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'PUT',
      url,
      data
    })
  }

  // DELETE请求
  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: 'DELETE',
      url
    })
  }

  // 上传文件
  async upload<T = any>(url: string, file: File, fieldName = 'file'): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append(fieldName, file)
    
    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// 创建单例实例
export const api = new ApiClient()

// 导出常用方法
export const { get, post, put, delete: del, upload } = api
